import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '@/context/UserContext';
import { 
  Languages, 
  Calculator, 
  History, 
  Palette, 
  ChevronRight,
  Lock,
  CheckCircle,
  PlayCircle
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const COURSES = [
  {
    id: 'memeglish',
    name: 'MEMEGLISH',
    subtitle: 'Vocabulary & Terminology',
    description: 'Master the language of the trenches. From "ape" to "rug", speak fluent degen.',
    icon: Languages,
    color: 'text-pump-green',
    bgColor: 'bg-pump-green/20',
    slides: 15,
    duration: '20 min'
  },
  {
    id: 'memethematics',
    name: 'MEMETHEMATICS',
    subtitle: 'Numbers & Analytics',
    description: 'Learn to read charts, understand market cap, and analyze like a pro.',
    icon: Calculator,
    color: 'text-degen-purple',
    bgColor: 'bg-degen-purple/20',
    slides: 11,
    duration: '25 min'
  },
  {
    id: 'memestory',
    name: 'MEMESTORY',
    subtitle: 'KOL History & Legends',
    description: 'Study the legends who shaped pump.fun. Their stories, their strategies.',
    icon: History,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/20',
    slides: 8,
    duration: '15 min'
  },
  {
    id: 'memeart',
    name: 'MEMEART',
    subtitle: 'NFT History & Creation',
    description: 'Explore the top NFTs of 2020-2021 and learn how they work.',
    icon: Palette,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/20',
    slides: 12,
    duration: '30 min'
  }
];

const ClassesPage = () => {
  const { user } = useUser();

  const getProgress = (courseId) => {
    if (!user?.progress?.[courseId]) return { completed: false, quiz_passed: false, current_slide: 0 };
    return user.progress[courseId];
  };

  const isCourseLocked = (index) => {
    if (!user) return false;
    if (index === 0) return false;
    return !getProgress(COURSES[index - 1].id).quiz_passed;
  };

  const getCourseStatus = (courseId, index) => {
    const progress = getProgress(courseId);
    if (progress.quiz_passed) return 'completed';
    if (isCourseLocked(index)) return 'locked';
    if (progress.current_slide > 0) return 'in-progress';
    return 'available';
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-unbounded font-bold text-4xl md:text-5xl text-white mb-4" data-testid="classes-title">
            YOUR <span className="text-pump-green">CLASSES</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Complete each course and pass the quiz to unlock the next
          </p>
        </motion.div>

        {/* Progress Overview */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-medium">Overall Progress</span>
              <span className="text-pump-green font-mono">
                {COURSES.filter(c => getProgress(c.id).quiz_passed).length} / {COURSES.length} completed
              </span>
            </div>
            <Progress 
              value={(COURSES.filter(c => getProgress(c.id).quiz_passed).length / COURSES.length) * 100}
              className="h-3 bg-pump-surface"
            />
          </motion.div>
        )}

        {/* Course List */}
        <div className="space-y-4">
          {COURSES.map((course, index) => {
            const status = getCourseStatus(course.id, index);
            const progress = getProgress(course.id);
            const slideProgress = course.slides > 0 ? (progress.current_slide / course.slides) * 100 : 0;

            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={status === 'locked' ? '#' : `/course/${course.id}`}
                  data-testid={`class-item-${course.id}`}
                  className={`block group glass rounded-xl p-6 transition-all duration-300 ${
                    status === 'locked'
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:border-pump-green/50 hover:shadow-neon'
                  }`}
                >
                  <div className="flex items-center gap-6">
                    {/* Course Number */}
                    <div className="hidden sm:flex w-12 h-12 rounded-xl bg-pump-surface items-center justify-center shrink-0">
                      <span className="font-unbounded font-bold text-zinc-500">{String(index + 1).padStart(2, '0')}</span>
                    </div>

                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl ${course.bgColor} flex items-center justify-center shrink-0`}>
                      <course.icon className={course.color} size={24} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-unbounded font-bold text-lg text-white group-hover:text-pump-green transition-colors">
                          {course.name}
                        </h3>
                        {status === 'completed' && (
                          <CheckCircle className="text-pump-green shrink-0" size={18} />
                        )}
                        {status === 'locked' && (
                          <Lock className="text-zinc-500 shrink-0" size={18} />
                        )}
                      </div>
                      <p className="text-zinc-500 text-sm mb-2">{course.subtitle}</p>
                      
                      {/* Progress Bar (if in progress) */}
                      {status === 'in-progress' && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                            <span>Progress</span>
                            <span>{Math.round(slideProgress)}%</span>
                          </div>
                          <div className="h-1.5 bg-pump-surface rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-pump-green rounded-full"
                              style={{ width: `${slideProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Meta & Action */}
                    <div className="hidden md:flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-sm text-zinc-500">{course.slides} slides</div>
                        <div className="text-xs text-zinc-600">{course.duration}</div>
                      </div>
                      
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        status === 'locked' ? 'bg-zinc-800' : 'bg-pump-green/20 group-hover:bg-pump-green/30'
                      } transition-colors`}>
                        {status === 'locked' ? (
                          <Lock className="text-zinc-500" size={18} />
                        ) : status === 'completed' ? (
                          <CheckCircle className="text-pump-green" size={18} />
                        ) : (
                          <PlayCircle className="text-pump-green" size={18} />
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 glass rounded-2xl p-6 border border-degen-purple/30"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-degen-purple/20 flex items-center justify-center shrink-0">
              <span className="text-degen-purple">?</span>
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">How it works</h4>
              <p className="text-zinc-400 text-sm">
                Complete each course by reading all slides and passing the quiz with 60% or higher.
                Courses unlock sequentially - you must complete the previous course to access the next.
                Once all 4 courses are complete, you'll earn your Bachelor of Pumpology NFT certificate!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ClassesPage;
